import { JsonObj } from '@mhp/general-interface';
import { TermAcceptance } from 'models/consumer';
import { PreferredLocation } from 'models/provider';
import { Nullable, RequestState } from 'modules/types/common';

export interface SpecialityInterface {
  id: string;
  nameLong: string;
  nameShort: string;
}

export interface PreferredProviderInterface {
  resourceId: string;
  resourceDisplay: string;
  personnelId: number | string;
  providerProfileImageUrl: string;
  specialitySet: Array<SpecialityInterface>;
  primary: boolean;
}

export interface MappedProviders {
  id: string;
  name: string;
  personnelId: number;
  specialties: SpecialityInterface[];
  profileImageUrl: string;
  resourceDisplay: string;
  primary: boolean;
}

export interface Profile {
  consumer: Consumer;
  grantors: Grantor[];
  grantees: Grantee[];
  preferredProviders: MappedProviders[];
  insurances: Insurance[];
}

export interface Consumer {
  cernerPersonId?: string;
  consumerId: string;
  pingUserId: string;
  umpiLinkId: string;
  firstName: string;
  lastName: string;
  middleName: string;
  displayName?: string;
  givenName: string;
  dateOfBirth: string;
  emails: Email[];
  phones: Phone[];
  addresses: Address[];
  age: number;
  emancipatedMinor: boolean;
  ethnicity?: string;
  gender?: 'Female' | 'Male';
  mrn?: string;
  userName?: string;
  shSubscriberNumber?: string;
  ssn?: string;
  externalTermsAcceptance: TermAcceptance[];
  termAcceptance: TermAcceptance;
  doOnboarding: boolean;
  appCacheKey: string;
  newAppCacheKey: string;
  isInMillenium?: boolean;
  systemIds?: object;
}

export interface Email {
  primary: boolean;
  value: string;
  verified: boolean;
  type: string;
}

export interface Phone {
  primary: boolean;
  value: string;
  verified: boolean;
  type: 'HOME' | 'MOBILE' | 'WORK';
}

export interface Address {
  streetAddress: string;
  streetAddress2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  type: string;
  primary: boolean;
}

export interface RequestedProxy {
  id: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  gender: string;
  birthDate: string;
  relationship: string;
  address: string;
  otherRelationship?: string;
  street?: string;
  housingType?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

export interface Grantor {
  consumerId: string;
  umpiLinkId: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  middleName?: string;
  gender: 'Female' | 'Male';
  pingUserId: string;
  age?: number;
  cernerPersonId?: string;
  dateOfBirth: string;
  relationship: string;
  startDate: string;
  isInMillenium: boolean;
}

export interface Grantee {
  consumerId: string;
  umpiLinkId: string;
  firstName: string;
  lastName: string;
  middleName: string;
  displayName: string;
  gender: 'Female' | 'Male';
  pingUserId: string;
  relationship: string;
  startDate: string;
}

export type LinkedConsumer = Grantor | Grantee;

export enum InsuranceSource {
  MILLENNIUM = 'Millennium',
  SELECT_HEALTH = 'Facets'
}

export interface Insurance {
  address?: Address;
  category?: string; // Medical,Dental,Vision,Medicare etc.
  sequence: number;
  insuranceName: string;
  subscriberId: string;
  subscriberName: string;
  groupId: string;
  employer: string;
  startDate: string;
  endDate: string;
  phones?: Phone[];
  planId: string;
  planName: string;
  planCode: string;
  productId?: string;
  networkId: string;
  payerId: string;
  payerName?: string;
  subscriber: Subscriber;
  members: Member[];
  source: InsuranceSource;
  primary: boolean;
  isProxy: boolean;
  isActive: boolean;
}

export interface Subscriber {
  consumerId: string;
  umpiLinkId: string;
  firstName: string;
  lastName: string;
  middleName: string;
  displayName: string;
  subscriberId: string;
  relationship: string;
  dateOfBirth: string;
}

export interface Member {
  consumerId: string;
  umpiLinkId: string;
  firstName: string;
  lastName: string;
  middleName: string;
  dateOfBirth: string;
  displayName: string;
  subscriberId: string;
  relationship: string;
  suffix?: string;
}

export interface ConsumerSubscriber {
  consumerId: string;
  subscriberId: string;
  suffix?: string;
}

export interface SendPinProps {
  type: 'email' | 'sms';
  phone?: string;
  email?: string;
}

export interface ValidatePinProps extends SendPinProps {
  pin: string;
}

export interface ConsumerTerms {
  type: string;
  version: string;
  date: string;
}

export interface CustomAttribute {
  name: string;
  value: number | string | JsonObj;
}

export interface ProfileState {
  isHydrated: boolean;
  isFetching: boolean;
  error: Error | null;
  insError: Error | null;
  insuranceRequestState: RequestState;
  visitpayEnrolledAndAccessible: boolean;
  visitpayEnrolledRequestState: RequestState;
  lastUpdated: number | null;
  customAttributes: CustomAttribute[];
  grantors: Grantor[];
  grantees: Grantee[];
  requestedProxies: RequestedProxy[];
  proxyToUpdate: RequestedProxy;
  activeInsurance: Nullable<Insurance>;
  insurances: Insurance[];
  preferredProviders: MappedProviders[];
  preferredLocations: PreferredLocation[];
  consumer: Consumer;
  validation: {
    status: Nullable<string>;
    type: Nullable<string>;
    error: Nullable<{
      type: string;
      message: string;
    }>;
  };
  profileRequestState: RequestState;
}
